/**
 * LeebReadingsInput — 12-box Joint Surface Leeb Hardness input with
 * real-time trimmed-mean calculation and expand/collapse.
 */
import { useState } from 'react';
import { Box, Collapse, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';

interface Props {
  position: 'Top' | 'Bottom';
  readings: (number | null)[];
  onChange: (readings: (number | null)[]) => void;
  disabled?: boolean;
}

function trimmedMean(vals: (number | null)[]): { mean: number | null; min: number | null; max: number | null; used: number; total: number } {
  const nums = vals.filter((v): v is number => v != null && !isNaN(v));
  if (nums.length === 0) return { mean: null, min: null, max: null, used: 0, total: 0 };
  if (nums.length <= 2) {
    return { mean: Math.round((nums.reduce((a, b) => a + b, 0) / nums.length) * 10) / 10, min: null, max: null, used: nums.length, total: nums.length };
  }
  const sorted = [...nums].sort((a, b) => a - b);
  const min = sorted[0];
  const max = sorted[sorted.length - 1];
  const trimmed = [...sorted];
  trimmed.splice(trimmed.indexOf(min), 1);
  trimmed.splice(trimmed.lastIndexOf(max), 1);
  return {
    mean: Math.round((trimmed.reduce((a, b) => a + b, 0) / trimmed.length) * 10) / 10,
    min, max, used: trimmed.length, total: nums.length,
  };
}

export const LeebReadingsInput = ({ position, readings, onChange, disabled = false }: Props) => {
  const theme = useTheme();
  const [open, setOpen] = useState(false);

  // Local string state for each input — avoids controlled-input number/string mismatch
  const [texts, setTexts] = useState<string[]>(() =>
    Array.from({ length: 12 }, (_, i) => readings[i] != null ? String(readings[i]) : '')
  );

  // Sync from parent if readings change externally (e.g. fetch from server)
  const readingsKey = readings.map((r) => r ?? '').join(',');
  useState(() => {}); // placeholder to keep hook order
  // Use useEffect-like pattern via key comparison
  const [lastKey, setLastKey] = useState(readingsKey);
  if (readingsKey !== lastKey) {
    setLastKey(readingsKey);
    setTexts(Array.from({ length: 12 }, (_, i) => readings[i] != null ? String(readings[i]) : ''));
  }

  const numericSlots: (number | null)[] = texts.map((t) => t === '' ? null : Number(t));
  const stats = trimmedMean(numericSlots);

  const handleChange = (index: number, raw: string) => {
    // Allow only digits, decimal, minus
    const clean = raw.replace(/[^0-9.\-]/g, '');
    const nextTexts = [...texts];
    nextTexts[index] = clean;
    setTexts(nextTexts);
    // Propagate numeric values to parent
    const nextNums = nextTexts.map((t) => t === '' ? null : Number(t));
    onChange(nextNums);
  };

  return (
    <Box>
      {/* Collapsed row: shows mean + expand toggle */}
      <Box
        onClick={() => setOpen((p) => !p)}
        sx={{
          display: 'flex', alignItems: 'center', gap: 0.5,
          cursor: 'pointer', px: 0.5, py: 0.25, borderRadius: 1,
          '&:hover': { backgroundColor: alpha(theme.palette.primary.main, 0.04) },
        }}
      >
        <Typography sx={{
          fontSize: '0.78rem', fontWeight: 600, flex: 1, textAlign: 'center',
          color: stats.mean != null ? 'text.primary' : 'text.disabled',
        }}>
          {stats.mean != null ? stats.mean : '\u2014'}
        </Typography>
        <Tooltip title={open ? 'Collapse readings' : 'Expand 12 readings'}>
          <IconButton size="small" sx={{ p: 0.25 }}>
            {open ? <ExpandLess sx={{ fontSize: 16 }} /> : <ExpandMore sx={{ fontSize: 16 }} />}
          </IconButton>
        </Tooltip>
      </Box>

      {/* Expanded: 12 input boxes */}
      <Collapse in={open}>
        <Box sx={{
          mt: 0.5, p: 1, borderRadius: 1.5,
          backgroundColor: alpha(theme.palette.background.default, 0.6),
          border: `1px solid ${alpha(theme.border.color, 0.2)}`,
        }}>
          <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '0.65rem', color: 'text.secondary', mb: 0.5, display: 'block' }}>
            Joint Surface — {position}
            {stats.mean != null && (
              <Typography component="span" sx={{ ml: 1, fontWeight: 700, color: 'text.primary', fontSize: '0.7rem' }}>
                Mean: {stats.mean}
              </Typography>
            )}
          </Typography>

          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 0.5 }}>
            {texts.map((txt, i) => {
              const num = txt === '' ? null : Number(txt);
              const isMin = stats.min != null && num === stats.min && stats.total >= 3;
              const isMax = stats.max != null && num === stats.max && stats.total >= 3;
              const isExcluded = (isMin && numericSlots.indexOf(stats.min) === i) ||
                (isMax && numericSlots.lastIndexOf(stats.max) === i);

              return (
                <TextField
                  key={i}
                  size="small"
                  placeholder={`#${i + 1}`}
                  value={txt}
                  inputProps={{ inputMode: 'decimal' }}
                  disabled={disabled}
                  onChange={(e) => handleChange(i, e.target.value)}
                  sx={{
                    '& .MuiInputBase-input': {
                      py: 0.4, px: 0.5, fontSize: '0.72rem', textAlign: 'center',
                      color: isExcluded ? theme.palette.text.disabled : undefined,
                      textDecoration: isExcluded ? 'line-through' : undefined,
                      '&::-webkit-outer-spin-button, &::-webkit-inner-spin-button': { WebkitAppearance: 'none' },
                      MozAppearance: 'textfield',
                    },
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: isExcluded
                        ? alpha(theme.palette.warning.main, 0.3)
                        : alpha(theme.border.color, 0.3),
                    },
                  }}
                />
              );
            })}
          </Box>

          {/* Stats footer */}
          {stats.total >= 3 && (
            <Typography variant="caption" sx={{ mt: 0.5, display: 'block', fontSize: '0.6rem', color: 'text.secondary' }}>
              Min: {stats.min} (excluded) &nbsp;·&nbsp; Max: {stats.max} (excluded) &nbsp;·&nbsp; {stats.used} of {stats.total} values used
            </Typography>
          )}
          {stats.total > 0 && stats.total < 3 && (
            <Typography variant="caption" sx={{ mt: 0.5, display: 'block', fontSize: '0.6rem', color: 'warning.main' }}>
              Enter at least 3 readings for trimmed mean
            </Typography>
          )}
        </Box>
      </Collapse>
    </Box>
  );
};
